import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCoverflow, Keyboard, Mousewheel } from "swiper/modules";
import type { Swiper as SwiperClass } from "swiper/types";

import "swiper/css";
import "swiper/css/effect-coverflow";

import type { FollowUser } from "../../../app/services/follows";
import { useFollowToggle } from "../../../hooks/useFollowToggle";
import { getUserDisplayName } from "../../../utils/user/displayName";
import { useRecipientCandidates } from "../useRecipientCandidates";
import Input from "../../form-item/Input";
import Button from "../../button/Button";
import ToolTip from "../../tooltip/ToolTip";
// @ts-expect-error - JSX のため型定義なし
import ListBlock from "../../block/ListBlock";
import RadioCardGroup from "../../form-item/RadioCardGroup";
import { STEP_FLOW_CONTENT_MAX_WIDTH } from "../../step-flow/stepFlowActions";
import EmailInviteModal from "../EmailInviteModal";
import type { OfferWheelLayout } from "../useOfferWheelLayout";
import { useDeferredReclick } from "../useDeferredReclick";
import type { OfferRecipientState } from "../types";
import {
  CARD_WIDTH_PX,
  CARD_HEIGHT_PX,
  COVERFLOW_AVATAR_SIZE_PX,
  ARC_AVATAR_SIZE_PX,
  CardWrapper,
  CardScaleCanvas,
  cardScaleFor,
  OfferUserCardFrame as UserCard,
  CardLabel,
  RotatedDisplayNameEn,
  OfferUserCardBody,
} from "../OfferUserCard";

export interface RecipientStepUserSelected {
  userId: number;
  displayName: string;
  avatarUrl: string | null;
}

/**
 * 「メールアドレスで招待」カードを表す番兵 ID。
 * 実在ユーザー ID（正の整数）と衝突しないよう負値を使う。フォローが 0 人でも
 * このカードは候補に必ず含め、未登録ユーザーへのメール招待を常に選べるようにする。
 */
const EMAIL_INVITE_CARD_ID = -1;

/**
 * `RadioCardGroup` の items は `FollowUser` 配列なので、メール招待カードも
 * 同じ形（最低限のフィールド）を満たす番兵オブジェクトとして 1 枚混ぜる。
 * 表示は `renderCard` 側で `id === EMAIL_INVITE_CARD_ID` を検出して専用カードに
 * 差し替えるため、ここの各フィールドは描画に使われない。
 */
const EMAIL_INVITE_SENTINEL: FollowUser = {
  id: EMAIL_INVITE_CARD_ID,
  display_name_jp: "",
  display_name_en: "",
  avatar_url: null,
  title_jp: "",
  title_en: "",
  affiliation_jp: "",
  affiliation_en: "",
  is_following: false,
  is_blocked: false,
  offer_criteria: null,
  capacity_index: null,
  capacity_index_updated_at: null,
  schedule_overview: null,
  upcoming_schedule: null,
  vanity_slug: null,
  // 型充足のためのダミー値。受託しないロールの除外は候補取得側
  //（useRecipientCandidates）で済んでいるため、値は何でもよい。
  role: "CREATOR",
};

interface RecipientStepProps {
  recipient: OfferRecipientState;
  onChange: (next: OfferRecipientState) => void;
  /** 受信者ユーザーが確定したときに呼ばれる（サマリ更新用） */
  onUserSelected?: (info: RecipientStepUserSelected) => void;
  /**
   * arc モードで、選択済みの「メールアドレスで招待」カードを再クリックしたときに
   * 呼ばれる。入力済みのメールアドレスを編集する導線で、親（OfferWizardOverlay）が
   * 入力モーダルを開く。未入力のまま次へ進んだときのモーダル起動も親側が担う。
   */
  onRequestEmailInput?: () => void;
  /** Shift ホールドのスタックプレビュー開始 / 解除通知（副パネルを隠す用途）。 */
  // eslint-disable-next-line no-unused-vars
  onStackChange?: (active: boolean) => void;
  /**
   * カードのダブルクリック / Enter で「次へ」相当の決定を行う。シングルクリックと
   * スクロールは選択のみなので、決定はここと ActionPanel の「次へ」だけに限定する。
   */
  onConfirmSelection?: () => void;
  /**
   * arc モードのカード寸法 / 縦中心。空いている領域へフィットさせた値を親
   *（`OfferWizardOverlay`）が算出して渡す。`null` のときは基準寸法で描く。
   */
  wheelLayout?: OfferWheelLayout | null;
  /**
   * ロール先順序のとき、カルーセルの候補を「このロールをアクティブに
   * しているユーザー（フォロー中 + accepting_offers ON のフォロー外）」に
   * 絞り込む。`undefined` のときは `/api/me/follows` のフォロー中一覧を表示。
   */
  roleFilterRoleId?: number;
  /**
   * カード並びの表示モード。
   *  - "coverflow"（既定）: 既存の Swiper Coverflow カルーセル
   *  - "arc": 円形/扇形 → ホイール回転のスクロール演出（RadioCardGroup）
   */
  variant?: "coverflow" | "arc";
}

// カード寸法 / アバターサイズ定数・カード見た目（CardWrapper / UserCard /
// CardLabel / RotatedDisplayNameEn / OfferUserCardBody）は ../OfferUserCard に
// 共通化済み（オンボーディングのプレビューと同一デザインを共有する）。

// ===== Styled Components =====

const StepRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

/**
 * arc モードで検索フィールドを OverlayBody 上端に sticky させるラッパ。
 * ロールステップの `StickyTagBar`（FilterChipBar）と同じ役割・同じ z-index で、
 * 円弧アニメーションのカード（Stage: z=2）に覆われないようにする。
 */
const StickySearchBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 3;
  /* OverlayBody の左右 padding が 0 なので、自分でわずかな padding を確保。 */
  padding: 4px 0 8px;
  /* 下部の ActionPanel（WizardActionPanelInner）と同じ幅上限で中央寄せ。 */
  width: 100%;
  max-width: ${STEP_FLOW_CONTENT_MAX_WIDTH}px;
  margin-inline: auto;
`;

const CarouselWrapper = styled.div`
  position: relative;
  width: 100%;
  /* Coverflow で奥行き方向に回転した左右スライドや transform が
     親コンテナ外に出てページ水平スクロールが発生するのを防ぐ。 */
  overflow: hidden;

  /* Swiper のスタイルをこのステップ内に閉じる（他箇所へのグローバル波及を
     避ける）。 */
  .swiper {
    width: 100%;
    /* ブックマークアイコンが top: -4px でカード上端から少し外に出るぶんを
       padding-top で内側に吸収し、Swiper の overflow: hidden でクリップ
       されないようにする。 */
    padding: 16px 0 12px;
  }

  .swiper-slide {
    /* 縦長白銀比（1 : sqrt(2) = 1 : 1.414）。
       Swiper Coverflow + slidesPerView auto では aspect-ratio が
       効かないケースがあるため width / height とも明示する。 */
    width: ${CARD_WIDTH_PX}px;
    height: ${CARD_HEIGHT_PX}px;
    display: flex;
    align-items: stretch;
    justify-content: stretch;
    /* スライド内に置いた BookmarkButton の top: -4px のはみ出しを許可。 */
    overflow: visible;
  }
`;

/**
 * カード右上のブックマーク。位置・色・サイズは
 * HOME 完了ショット（`AllShotsList.jsx` の `BookmarkIcon`）に合わせ、
 * カード上端から少しはみ出して「カード上に乗る」見た目にする。
 */
const BookmarkButton = styled.button`
  position: absolute;
  top: -4px;
  right: 4px;
  z-index: 1;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  line-height: 1;
  cursor: pointer;
  transition: transform 0.15s;

  &:hover {
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: progress;
  }
`;

/**
 * Material Symbols のフォントクラスは color の継承が
 * 親要素の指定を素直に拾わない環境があるため、span に直接 color を当てる
 * （HOME 完了ショットの `BookmarkIcon` と同じ実装）。
 */
const BookmarkSymbol = styled.span.attrs({
  className: "material-symbols-rounded",
})<{ $active: boolean }>`
  font-size: 32px;
  /* ON: soremo-blue / OFF: #f0f0f0（薄いグレー、デザイン指定） */
  color: ${(p) => (p.$active ? "var(--soremo-blue)" : "#f0f0f0")};
  transition: color 0.15s;
`;

const EmptyState = styled.div.attrs({ className: "bodytext-13" })`
  padding: 24px 0;
  text-align: center;
  color: var(--soremo-light-gray);
`;

/** 検索欄の直下に出す補足（ヒット 0 件 / 取得上限に達した旨）。 */
const SearchNotice = styled.p.attrs({ className: "bodytext-11" })`
  margin: 6px 0 0;
  text-align: center;
  color: var(--soremo-light-gray);
`;

const InviteWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InviteCancelRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;

/**
 * メール招待カードのアイコン。ユーザーカードの Avatar 位置に相当する見出し記号。
 * 選択中は soremo-blue、未選択はやや薄いグレーで表示する。
 */
const EmailInviteIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})<{ $active: boolean }>`
  font-size: 56px;
  color: ${(p) =>
    p.$active ? "var(--soremo-blue)" : "var(--soremo-light-gray)"};
  transition: color 0.15s;
`;

// ===== 本体 =====

const RecipientStep: React.FC<RecipientStepProps> = ({
  recipient,
  onChange,
  onUserSelected,
  onRequestEmailInput,
  onStackChange,
  onConfirmSelection,
  wheelLayout,
  roleFilterRoleId,
  variant = "coverflow",
}) => {
  // 「メールアドレスで招待」はメール未入力でもカードとして選択済みになる
  // （入力は「次へ」で開くモーダルに委ねる）。
  const isEmailSelected = recipient.mode === "email";

  // 送信先の絞り込みクエリ（arc モードのみ表示）。候補は母集団の全ページを
  // 取得済みなので、この絞り込みは「全員」を対象にしたクライアント側フィルタになる。
  const [query, setQuery] = useState("");
  const stepRootRef = useRef<HTMLDivElement>(null);

  const { candidates, isLoading, reachedLimit } = useRecipientCandidates({
    roleFilterRoleId,
    query: variant === "arc" ? query : "",
  });

  const trimmedQuery = query.trim();
  const noSearchHit =
    variant === "arc" && !isLoading && !!trimmedQuery && candidates.length === 0;

  /**
   * 候補が入れ替わったら、最寄りの `[data-offer-overlay-scroll]` を先頭へ戻して
   * `RadioCardGroup` を初期円形配置から描き直させる（ロールタグ切替と同じ挙動）。
   */
  useEffect(() => {
    if (variant !== "arc") return;
    const scrollerEl = stepRootRef.current?.closest(
      "[data-offer-overlay-scroll]",
    );
    if (!(scrollerEl instanceof HTMLElement)) return;
    if (scrollerEl.scrollTop <= 0) return;
    scrollerEl.scrollTo({ top: 0, behavior: "smooth" });
  }, [query, variant]);

  /**
   * 「メールアドレスで招待」カードが選択された（クリック / 中央スクロール / 左右キー）。
   * ユーザーカードと同じく「選択したら他の選択は外れる」ので、`mode` を email に
   * 倒すだけでユーザー選択は解除される。入力済みのメール / 表示名は次へ進むときの
   * モーダル初期値として残す。
   */
  const handleEmailCardSelect = useCallback(() => {
    if (recipient.mode === "email") return; // 既に選択済み（重複通知は無視）
    onChange({ mode: "email" });
  }, [recipient.mode, onChange]);

  return (
    <StepRoot ref={stepRootRef}>
      {variant === "arc" && (
        <StickySearchBar>
          <Input
            type="search"
            value={query}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setQuery(e.target.value)
            }
            placeholder="名前・肩書き・所属で検索..."
          />
          {noSearchHit && (
            <SearchNotice>
              「{trimmedQuery}」に一致するユーザーはいません
            </SearchNotice>
          )}
          {reachedLimit && (
            <SearchNotice>
              候補が多いため一部のみ読み込んでいます
            </SearchNotice>
          )}
        </StickySearchBar>
      )}
      <RecipientPicker
        selectedUserId={
          recipient.mode === "follows" ? recipient.userId : undefined
        }
        emailSelected={isEmailSelected}
        emailLabel={recipient.email}
        onSelectEmailInvite={handleEmailCardSelect}
        onRequestEmailInput={onRequestEmailInput}
        candidates={candidates}
        isLoading={isLoading}
        roleFilterRoleId={roleFilterRoleId}
        variant={variant}
        onStackChange={onStackChange}
        onConfirmSelection={onConfirmSelection}
        wheelLayout={wheelLayout}
        onSelect={(user) => {
          onChange({ mode: "follows", userId: user.userId });
          onUserSelected?.(user);
        }}
        onDeselect={() => {
          // 「誰も選択していない」状態に戻す（無選択スタートと同じ）。
          // モードは follows のまま保ち、userId のみクリアする。
          onChange({ mode: "follows" });
        }}
      />
      {/*
       * 旧 coverflow / list variant では従来どおり ListBlock ベースの
       * InvitePicker を残す（メアド招待をその場でキープする挙動）。
       * arc モードでは「メールアドレスで招待」をカード化したため、ここには出さず
       * カード選択 →「次へ」→ モーダル → 次ステップ遷移の動線にする。
       */}
      {variant !== "arc" && (
        <InvitePicker recipient={recipient} onChange={onChange} />
      )}
    </StepRoot>
  );
};

// ===== サブ: 送信先候補ピッカー =====

interface RecipientPickerProps {
  selectedUserId?: number;
  /** email モードで「メールアドレスで招待」カードが選択中（チェック表示）かどうか。 */
  emailSelected?: boolean;
  /** email モードで確定済みの招待先メールアドレス（カードのラベル表示に使う）。 */
  emailLabel?: string;
  /** 「メールアドレスで招待」カードが選択されたとき（ユーザー選択と同じ扱い）。 */
  onSelectEmailInvite?: () => void;
  /** 選択済みのメール招待カードを再クリックしたとき（アドレスの編集）。 */
  onRequestEmailInput?: () => void;
  /** 除外・検索フィルタ適用済みの送信先候補（{@link useRecipientCandidates}）。 */
  candidates: FollowUser[];
  /** 候補の全ページ取得が完了するまで `true`。 */
  isLoading: boolean;
  /** Shift ホールドのスタックプレビュー開始 / 解除通知。 */
  // eslint-disable-next-line no-unused-vars
  onStackChange?: (active: boolean) => void;
  /** カードのダブルクリック / Enter で「次へ」相当の決定を行う。 */
  onConfirmSelection?: () => void;
  /** arc モードのカード寸法 / 縦中心（空き領域へフィットさせた値）。 */
  wheelLayout?: OfferWheelLayout | null;
  onSelect: (info: RecipientStepUserSelected) => void;
  /**
   * カード外（カルーセル背景）クリックで「誰も選択しない」状態に戻すコールバック。
   * ロール先動線（無選択のままステッパーで「ロール」に進む）を残すために必要。
   */
  onDeselect: () => void;
  /**
   * 指定すると、フォロー中ユーザーではなく
   * 「このロールをアクティブにしているユーザー」を候補として表示する。
   * 並び順はフォロー中 → accepting_offers が ON のユーザーで、ID 重複は除外する。
   */
  roleFilterRoleId?: number;
  /** "coverflow"（既存）か "arc"（円形/扇形 → ホイール回転スクロール演出）。 */
  variant?: "coverflow" | "arc";
}

const buildSelectedInfo = (u: FollowUser): RecipientStepUserSelected => ({
  userId: u.id,
  displayName: getUserDisplayName(u),
  avatarUrl: u.avatar_url,
});

/**
 * 送信先候補のカード群。ロールステップの {@link ../RolePicker} と対になる存在で、
 * 候補の取得・除外・検索フィルタは {@link useRecipientCandidates} が担い、
 * ここは並べ方を `variant` に委ねる:
 *  - `"arc"`: `RadioCardGroup`（円形/扇形 → ホイール回転）。末尾に
 *    「メールアドレスで招待」カードを常駐させる。
 *  - `"coverflow"`: 旧 Swiper Coverflow カルーセル。
 */
const RecipientPicker: React.FC<RecipientPickerProps> = ({
  selectedUserId,
  emailSelected = false,
  emailLabel,
  onSelectEmailInvite,
  onRequestEmailInput,
  candidates,
  isLoading,
  onStackChange,
  onConfirmSelection,
  wheelLayout,
  onSelect,
  onDeselect,
  roleFilterRoleId,
  variant = "coverflow",
}) => {
  const isRoleFiltered = roleFilterRoleId !== undefined;
  // メール招待カードの「再クリックで編集」は、ダブルクリック（=「次へ」）の
  // 1 打目で暴発しないよう少し遅延させて実行する。
  const reclick = useDeferredReclick();

  // 選択中ユーザーの index を初期スライドとして渡す（再マウント時のみ参照）。
  const initialSlideIndex = useMemo(() => {
    if (selectedUserId === undefined) return 0;
    const i = candidates.findIndex((u) => u.id === selectedUserId);
    return i >= 0 ? i : 0;
  }, [candidates, selectedUserId]);

  // スワイプ連打時の onChange 多発を抑えるため、直近通知済みの userId を保持する。
  const lastNotifiedUserIdRef = useRef<number | undefined>(selectedUserId);
  const swiperRef = useRef<SwiperClass | null>(null);

  // 親側の `selectedUserId` が変化した（例: 背景クリックで undefined にリセット）
  // ときは、内部の重複防止ガード値も同期させる。
  useEffect(() => {
    lastNotifiedUserIdRef.current = selectedUserId;
  }, [selectedUserId]);

  const notifySelection = useCallback(
    (user: FollowUser) => {
      if (lastNotifiedUserIdRef.current === user.id) return;
      lastNotifiedUserIdRef.current = user.id;
      onSelect(buildSelectedInfo(user));
    },
    [onSelect],
  );

  // 起動時の自動選択はしない（無選択スタートで「ロール先動線」を残す）。
  // 中央スライド = 選択 のメンタルモデルは、ユーザー自身が操作した時点（
  // スワイプ/ホイール/キーボード/カードクリック）から発火する。

  const handleSlideChange = useCallback(
    (sw: SwiperClass) => {
      // 親側で deselect されている（= 無選択）状態のままなら、スライド移動だけでは
      // 自動選択しない。ユーザーが明示的にカードをクリック／ドラッグして
      // 中央に持ってきた時点で初めて選択扱いにする。
      if (selectedUserId === undefined) return;
      const u = candidates[sw.activeIndex];
      if (!u) return;
      notifySelection(u);
    },
    [candidates, notifySelection, selectedUserId],
  );

  const handleCardClick = useCallback(
    (index: number) => {
      const sw = swiperRef.current;
      const u = candidates[index];
      if (!u) return;
      // どの位置のカードでも、クリックされた時点で選択扱いにする。
      // 中央でない場合は併せてセンタリングする。
      if (sw && sw.activeIndex !== index) {
        sw.slideTo(index);
      }
      notifySelection(u);
    },
    [candidates, notifySelection],
  );

  /**
   * Swiper の wrapper 直下（slide 以外、つまりカード外の余白）クリックで
   * 「無選択」状態に戻す。カード本体クリックは `e.stopPropagation` で除外。
   * Swiper の drag/ホイール操作はクリックを発火しないため、誤発火しない。
   */
  const handleWrapperClick = useCallback(() => {
    if (selectedUserId === undefined) return;
    lastNotifiedUserIdRef.current = undefined;
    onDeselect();
  }, [onDeselect, selectedUserId]);

  if (isLoading) {
    return (
      <EmptyState>
        {isRoleFiltered
          ? "対象ロールのユーザーを読み込み中..."
          : "フォロー中ユーザーを読み込み中..."}
      </EmptyState>
    );
  }
  // ── arc モード: 円形/扇形 → ホイール回転のスクロール演出 ─────
  // RadioCardGroup 側の Slot がクリック / a11y / 位置決めを持つので、
  // 内側のカードは button にせず（ネスト focusable を避けるため）
  // ArcUserCard を div ベースで描画する。
  if (variant === "arc") {
    // フォローが 0 人でも「メールアドレスで招待」カードは必ず末尾に含める。
    const arcCandidates: FollowUser[] = [...candidates, EMAIL_INVITE_SENTINEL];
    const arcValue = emailSelected ? EMAIL_INVITE_CARD_ID : selectedUserId;
    // カード中身は基準寸法（256×362 = ポップオーバーのカード）で描き、
    // 実寸へ等比スケールする（フォント / アバター / 余白のバランス維持）。
    const arcCardScale = cardScaleFor(
      wheelLayout?.cardSize.width ?? CARD_WIDTH_PX,
    );
    return (
      <RadioCardGroup<FollowUser>
        items={arcCandidates}
        value={arcValue}
        onChange={(id, user) => {
          if (id === EMAIL_INVITE_CARD_ID) {
            // ユーザーカードと同じく「中央に来た = 選択」。メールアドレスの入力は
            //「次へ」で開くモーダルに任せ、ここでは選択状態だけを切り替える。
            lastNotifiedUserIdRef.current = undefined;
            onSelectEmailInvite?.();
            return;
          }
          notifySelection(user);
        }}
        // ダブルクリック / Enter の「決定」で「次へ」。ホーム・プロジェクトの
        // ホイールと操作感を揃える（あちらは決定で該当ページへ遷移する）。
        onConfirm={() => {
          reclick.cancel();
          onConfirmSelection?.();
        }}
        onDeselect={() => {
          // 旧 coverflow 版の handleWrapperClick と同じく、Stage 余白クリックで
          // 「誰も選択していない」状態に戻す。
          if (selectedUserId === undefined && !emailSelected) return;
          reclick.cancel();
          lastNotifiedUserIdRef.current = undefined;
          onDeselect();
        }}
        // カード寸法・縦中心は空いている領域へフィットさせた値を親から受ける
        // （未算出のフレームだけ基準寸法にフォールバック）。
        cardSize={
          wheelLayout?.cardSize ?? {
            width: CARD_WIDTH_PX,
            height: CARD_HEIGHT_PX,
          }
        }
        verticalCenterY={wheelLayout?.verticalCenterY}
        breakOut
        // カード列を右カラムの左右端までフルに見せる（クリップ余白なし）。
        breakOutInsetPx={0}
        // 扇の段階でもカードを等倍で見せる（ホーム / プロジェクトと同じ）。
        scaleFan={1}
        // ジェスチャ挙動を他のホイール（ホーム / プロジェクト / ロール）と
        // 統一する。内部スクロールを持つカードが無いため操作は素通しになる。
        cardScrollHandoff
        // Stage にフォーカスが無くても ← / → でカードを送れるようにする
        // （ホーム / プロジェクトのホイールと同じ操作感）。
        globalKeyboardNav
        // Shift ホールドで全カードを手札状の扇に集約して全体量を見渡す。
        // 件数は「メールアドレスで招待」センチネルを除いた実候補数を出す。
        stackPreview={{ count: candidates.length }}
        onStackChange={onStackChange}
        ariaLabel={
          isRoleFiltered ? "送信先ユーザーを選択" : "フォロー中ユーザーから選択"
        }
        renderCard={(user, state) => (
          <CardScaleCanvas $scale={arcCardScale}>
            {user.id === EMAIL_INVITE_CARD_ID ? (
              <EmailInviteCard
                selected={state.checked}
                email={emailLabel}
                // 再クリックによる編集モーダルは、ダブルクリックの 1 打目で
                // 一瞬開いてしまわないよう遅延実行する。
                onRequestInput={() =>
                  reclick.schedule(() => onRequestEmailInput?.())
                }
              />
            ) : (
              <ArcUserCard user={user} selected={state.checked} />
            )}
          </CardScaleCanvas>
        )}
      />
    );
  }

  // ── coverflow（旧）: 候補が無いときは空状態 ─────
  if (candidates.length === 0) {
    return (
      <EmptyState>
        {isRoleFiltered
          ? "このロールを設定しているユーザーがいません"
          : "フォロー中のユーザーがいません"}
      </EmptyState>
    );
  }

  return (
    <CarouselWrapper onClick={handleWrapperClick}>
      <Swiper
        modules={[EffectCoverflow, Mousewheel, Keyboard]}
        effect="coverflow"
        grabCursor
        centeredSlides
        slidesPerView="auto"
        coverflowEffect={{
          rotate: 30,
          stretch: 0,
          depth: 100,
          modifier: 1,
          slideShadows: false,
        }}
        mousewheel={{
          // 横方向の操作のみ受け付ける（縦スクロールを誤って消費しない）
          forceToAxis: true,
          // トラックパッドの微小な慣性で連打されないよう感度を控えめに
          sensitivity: 0.8,
          thresholdDelta: 6,
        }}
        keyboard={{
          enabled: true,
          // ビューポートに入っている時だけキーボード入力を拾う（他ステップに
          // 戻ったあと意図せず操作されないようにする保険）。
          onlyInViewport: true,
        }}
        initialSlide={initialSlideIndex}
        onSwiper={(sw) => {
          swiperRef.current = sw;
        }}
        onSlideChange={handleSlideChange}
      >
        {candidates.map((u, index) => (
          <SwiperSlide key={u.id}>
            <UserCardItem
              user={u}
              selected={selectedUserId === u.id}
              onClick={(e) => {
                // カード本体クリックは「選択」確定。背景クリックの deselect と
                // 衝突しないよう、ここで stopPropagation する。
                e.stopPropagation();
                handleCardClick(index);
              }}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </CarouselWrapper>
  );
};

// ===== サブ: ユーザーカード共通部品 =====

interface UserCardBodyProps {
  user: FollowUser;
  avatarSize: number;
  /** 選択中のカードでのみ公開プロフィールの QR を表示する（カードのスペース節約）。 */
  selected?: boolean;
}

/**
 * `FollowUser` を共通カード本体（{@link OfferUserCardBody}）の props にマップする
 * 薄いラッパー。表示デザインは ../OfferUserCard に一元化済み。
 */
const UserCardBody: React.FC<UserCardBodyProps> = ({
  user,
  avatarSize,
  selected = false,
}) => (
  <OfferUserCardBody
    displayName={getUserDisplayName(user)}
    title={user.title_jp || user.title_en || ""}
    affiliation={user.affiliation_jp || user.affiliation_en || ""}
    avatarUrl={user.avatar_url}
    avatarSize={avatarSize}
    selected={selected}
    profileUrl={user.vanity_slug ? `https://soremo.jp/${user.vanity_slug}` : ""}
  />
);

interface FollowBookmarkProps {
  userId: number;
  initialIsFollowing: boolean;
}

/**
 * カード右上のフォロー切替ブックマーク。
 *
 * フォロー状態は `useFollowToggle` のローカル state で管理し、楽観的更新で
 * アイコン色を即時反映する。`/api/me/follows` はミューテーション後に再取得
 * されない（仕様）ため、サーバの `is_following` を表示にそのまま使うと
 * OFF にしてもブックマークが青のままになってしまうのを防ぐ。
 *
 * カード本体（Slot / button）のクリックと衝突しないよう、`stopPropagation` で
 * 親のクリックを止める。`disabled` 中の操作は no-op。
 */
const FollowBookmark: React.FC<FollowBookmarkProps> = ({
  userId,
  initialIsFollowing,
}) => {
  const {
    isFollowing,
    toggle: toggleFollow,
    isLoading: isMutating,
  } = useFollowToggle(userId, initialIsFollowing);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (isMutating) return;
      void toggleFollow();
    },
    [toggleFollow, isMutating],
  );

  return (
    <ToolTip description={isFollowing ? "フォロー解除" : "フォローする"}>
      <BookmarkButton
        type="button"
        aria-label={isFollowing ? "フォロー解除" : "フォローする"}
        aria-pressed={isFollowing}
        disabled={isMutating}
        onClick={handleClick}
      >
        <BookmarkSymbol $active={isFollowing}>
          {isFollowing ? "bookmark" : "bookmark_border"}
        </BookmarkSymbol>
      </BookmarkButton>
    </ToolTip>
  );
};

// ===== サブ: coverflow モード用 ユーザーカード =====

interface UserCardItemProps {
  user: FollowUser;
  selected: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

const UserCardItem: React.FC<UserCardItemProps> = ({
  user,
  selected,
  onClick,
}) => (
  <CardWrapper>
    <UserCard type="button" $selected={selected} onClick={onClick}>
      <UserCardBody
        user={user}
        avatarSize={COVERFLOW_AVATAR_SIZE_PX}
        selected={selected}
      />
    </UserCard>
    <FollowBookmark userId={user.id} initialIsFollowing={user.is_following} />
  </CardWrapper>
);

// ===== サブ: arc モード用 ユーザーカード =====

interface ArcUserCardProps {
  user: FollowUser;
  selected: boolean;
}

/**
 * `RadioCardGroup` の Slot 内に置く「ボタンを含まない」ユーザーカード。
 * - Slot 自体が role=radio + onClick を持つので、ここでは `<button>` を使わない
 *   （ネストした focusable が radiogroup のキーボード操作と競合するのを避ける）。
 * - BookmarkButton だけは引き続き `<button>` で、`stopPropagation` で
 *   Slot 側の選択発火を抑止する（`FollowBookmark` 内で対処）。
 * - 左辺には `RotatedDisplayNameEn` でロールカード（右辺）と対になる縦書き
 *   英語名を装飾配置する（`display_name_en` が空のときは出さない）。
 */
const ArcUserCard: React.FC<ArcUserCardProps> = ({ user, selected }) => {
  const displayNameEn = (user.display_name_en ?? "").trim();

  return (
    <CardWrapper>
      <UserCard as="div" $selected={selected} $variant="arc">
        <UserCardBody
          user={user}
          avatarSize={ARC_AVATAR_SIZE_PX}
          selected={selected}
        />
      </UserCard>
      {displayNameEn && (
        <RotatedDisplayNameEn aria-hidden="true">
          {displayNameEn}
        </RotatedDisplayNameEn>
      )}
      <FollowBookmark userId={user.id} initialIsFollowing={user.is_following} />
    </CardWrapper>
  );
};

// ===== サブ: arc モード用 メール招待カード =====

interface EmailInviteCardProps {
  selected: boolean;
  /** 確定済みの招待先メール（あれば card 下部に表示）。 */
  email?: string;
  /** 選択済みの状態で再クリックされたとき（アドレスの編集）。 */
  onRequestInput: () => void;
}

/**
 * 「メールアドレスで招待」カード（arc モード）。
 * - フォローが 0 人でも常に末尾に並び、未登録ユーザーへのメール招待を選べる。
 * - ユーザーカードと同じ「クリックで選択」導線。アドレスの入力は選択後に
 *   「次へ」を押した時点でモーダルが開く（`OfferWizardOverlay` が担当）。
 * - 選択済みカードの再クリックだけは、入力済みアドレスを編集する導線として
 *   モーダルを開く（ロールカードの再クリックと同じ「二次アクション」の位置づけ）。
 * - `ArcUserCard` と同じく Slot 側がクリック / a11y を持つので `<button>` は使わず
 *   div ベースで描画する。
 */
const EmailInviteCard: React.FC<EmailInviteCardProps> = ({
  selected,
  email,
  onRequestInput,
}) => {
  const hasEmail = !!email;
  return (
    <CardWrapper>
      <UserCard
        as="div"
        $selected={selected}
        $variant="arc"
        onClick={() => {
          if (selected) onRequestInput();
        }}
      >
        <EmailInviteIcon $active={selected}>
          {hasEmail ? "forward_to_inbox" : "alternate_email"}
        </EmailInviteIcon>
        <CardLabel className="heading-spacing-18">
          メールアドレスで招待
        </CardLabel>
        {hasEmail ? (
          <CardLabel className="bodytext-13 u-line-height-200">
            {email}
          </CardLabel>
        ) : (
          <CardLabel className="bodytext-11 u-text-light-gray u-line-height-100">
            未登録の相手にも送れます
          </CardLabel>
        )}
      </UserCard>
    </CardWrapper>
  );
};

// ===== サブ: メアド招待（coverflow / list 用 ListBlock） =====

interface InvitePickerProps {
  recipient: OfferRecipientState;
  onChange: (next: OfferRecipientState) => void;
}

const InvitePicker: React.FC<InvitePickerProps> = ({
  recipient,
  onChange,
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  const isEmailMode = recipient.mode === "email" && !!recipient.email;

  const handleConfirm = useCallback(
    (email: string, username: string) => {
      onChange({ mode: "email", email, username: username || undefined });
      setModalOpen(false);
    },
    [onChange],
  );

  const handleCancelInvite = useCallback(() => {
    // メアド招待を取りやめてフォロー中モードに戻す。`userId` を持たない
    // ので無選択スタートと同じ扱いになる（ユーザーは改めてカードを選ぶ）。
    onChange({ mode: "follows" });
  }, [onChange]);

  const blockLabel = isEmailMode
    ? `招待中: ${recipient.email}`
    : "新しくユーザーを招待";

  return (
    <>
      <InviteWrapper>
        <ListBlock
          type="add"
          label={blockLabel}
          onClick={() => setModalOpen(true)}
        />
        {isEmailMode && (
          <InviteCancelRow>
            <Button
              size="small"
              variant="text"
              theme="secondary"
              onClick={handleCancelInvite}
            >
              招待を取り消す
            </Button>
          </InviteCancelRow>
        )}
      </InviteWrapper>

      {modalOpen && (
        <EmailInviteModal
          initialEmail={recipient.email ?? ""}
          initialUsername={recipient.username ?? ""}
          onClose={() => setModalOpen(false)}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
};

export default RecipientStep;
